import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, ArrowRightCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDate } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EstimateDialog } from "./EstimateDialog";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";

const TONE = { borrador: "outline", enviado: "secondary", aprobado: "default", rechazado: "destructive", vencido: "outline" };

export default function EstimatesPage() {
  const { t } = useT();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [estado, setEstado] = useState("all");
  const [newOpen, setNewOpen] = useState(false);
  const [convertTarget, setConvertTarget] = useState(null);

  const { data: estimates, isLoading } = useQuery({
    queryKey: ["estimates", estado],
    queryFn: async () => (await api.get("/estimates", {
      params: { page_size: 100, estado: estado === "all" ? undefined : estado },
    })).data.items,
  });

  const patch = useMutation({
    mutationFn: ({ id, estado: nuevoEstado }) => api.patch(`/estimates/${id}`, { estado: nuevoEstado }),
    onSuccess: () => { toast.success(t("estimates.updated")); qc.invalidateQueries({ queryKey: ["estimates"] }); },
  });

  const convert = useMutation({
    mutationFn: (id) => api.post(`/estimates/${id}/convert`),
    onSuccess: ({ data }) => {
      toast.success(t("estimates.converted", { code: data.work_order.code }));
      qc.invalidateQueries({ queryKey: ["estimates"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  return (
    <>
      <PageHeader testId="estimates-page-header" title={t("estimates.title")} subtitle={t("estimates.subtitle")}
        actions={can("estimates", "create") && (
          <Button data-testid="new-estimate-button" className="bg-brand hover:bg-brand-hover" onClick={() => setNewOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />{t("estimates.new")}
          </Button>
        )} />
      <div className="flex-1 overflow-y-auto p-6" data-testid="estimates-page">
        <div className="mb-4 flex justify-end">
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger data-testid="estimate-status-filter" className="h-8 w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("finance.settlements.all_statuses")}</SelectItem>
              {["borrador", "enviado", "aprobado", "rechazado", "vencido"].map((v) => (
                <SelectItem key={v} value={v}>{t(`estimates.status.${v}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-[10px] border border-line bg-white">
          {!isLoading && !estimates?.length ? (
            <p className="p-6 text-center text-[13px] text-ink-tertiary">{t("estimates.empty")}</p>
          ) : (
            <table className="w-full text-[12.5px]" data-testid="estimates-table">
              <thead className="bg-surface-sunken text-[11px] uppercase tracking-[0.04em] text-ink-tertiary">
                <tr>
                  <th className="px-3 py-2 text-left">{t("estimates.code")}</th>
                  <th className="px-3 py-2 text-left">{t("estimates.company")}</th>
                  <th className="px-3 py-2 text-left">{t("estimates.building")}</th>
                  <th className="px-3 py-2 text-left">{t("estimates.amount")}</th>
                  <th className="px-3 py-2 text-left">{t("estimates.seller")}</th>
                  <th className="px-3 py-2 text-left">{t("estimates.issued")}</th>
                  <th className="px-3 py-2 text-left">Estado</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {(estimates || []).map((e) => (
                  <tr key={e.id} className="border-b border-line" data-testid={`estimate-row-${e.id}`}>
                    <td className="px-3 py-1.5 font-medium">{e.code}</td>
                    <td className="px-3 py-1.5">{e.company_nombre}</td>
                    <td className="px-3 py-1.5">{e.property_nombre}</td>
                    <td className="px-3 py-1.5 tabular-nums">{formatMoney(e.monto_cents)}</td>
                    <td className="px-3 py-1.5">{e.vendedor_nombre}</td>
                    <td className="px-3 py-1.5">{formatDate(e.fecha_emision)}</td>
                    <td className="px-3 py-1.5"><Badge variant={TONE[e.estado]}>{t(`estimates.status.${e.estado}`)}</Badge></td>
                    <td className="px-3 py-1.5 text-right">
                      {e.estado === "borrador" && (
                        <button data-testid={`estimate-send-${e.id}`} className="text-[11.5px] text-brand"
                          onClick={() => patch.mutate({ id: e.id, estado: "enviado" })}>{t("estimates.mark_sent")}</button>
                      )}
                      {e.estado === "enviado" && (
                        <div className="flex justify-end gap-2">
                          <button data-testid={`estimate-approve-${e.id}`} className="text-[11.5px] text-state-ok"
                            onClick={() => patch.mutate({ id: e.id, estado: "aprobado" })}>{t("estimates.mark_approved")}</button>
                          <button data-testid={`estimate-reject-${e.id}`} className="text-[11.5px] text-state-critical"
                            onClick={() => patch.mutate({ id: e.id, estado: "rechazado" })}>{t("estimates.mark_rejected")}</button>
                        </div>
                      )}
                      {e.estado === "aprobado" && (
                        e.work_order_id ? (
                          <span className="text-[11.5px] text-ink-tertiary">{t("estimates.already_converted", { code: e.work_order_code })}</span>
                        ) : (
                          <button data-testid={`estimate-convert-${e.id}`} className="inline-flex items-center gap-1 text-[11.5px] font-medium text-brand"
                            onClick={() => setConvertTarget(e.id)}>
                            <ArrowRightCircle className="h-3.5 w-3.5" />{t("estimates.convert")}
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <EstimateDialog open={newOpen} onOpenChange={setNewOpen} />
      <ConfirmDialog testId="estimate-convert-confirm" open={Boolean(convertTarget)} onOpenChange={(v) => !v && setConvertTarget(null)}
        title={t("estimates.convert")} description={t("estimates.convert_confirm")}
        onConfirm={() => { convert.mutate(convertTarget); setConvertTarget(null); }} />
    </>
  );
}
